import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useGetComponentsQuery } from '../../redux/services/tanProfilesApi';
import { setSelected, setProfileMeta, setScannedBoothData, setScannedBoothComponents } from '../../redux/slice/tanProfileSlice';
import { setResumeTo, clearResumeTo } from '../../redux/slice/skinAnalysisSlice';
import quiz from '../../data/quiz.json';
import './QuizPage.css';

// Answers are stashed here before a "log in to save" redirect, then restored.
const RESUME_KEY = 'quizProfileAnswers';

const findOption = (qid, oid) => quiz.questions.find((q) => q.id === qid)?.options.find((o) => o.id === oid);
const optLabel = (qid, oid) => findOption(qid, oid)?.label;
const optReason = (qid, oid) => findOption(qid, oid)?.reason;
const fillTemplate = (tpl, vars) => (tpl || '').replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : `{${k}}`));

function computeShade(answers) {
  const glow = (answers.glow || [])[0];
  const depth = (answers.depth || [])[0];
  const undertones = answers.undertone || [];
  const depthOpt = findOption('depth', depth);
  let shade = 5;
  if (depthOpt) {
    if (glow === 'gradual') {
      shade = depthOpt.gradualBase;
    } else {
      const weights = undertones.map((uid) => findOption('undertone', uid)?.shadeWeight ?? 6);
      const avg = weights.length ? weights.reduce((a, b) => a + b, 0) / weights.length : 6;
      shade = Math.round(
        depthOpt.depthWeight * quiz.shadeCalculation.depthWeight +
        avg * quiz.shadeCalculation.undertoneWeight,
      );
    }
  }
  return Math.max(1, Math.min(12, shade));
}

// Display-only profile (labels + reasons from quiz.json) — needs no backend, so
// it works for logged-out users too.
function buildProfile(answers) {
  const glow = (answers.glow || [])[0];
  const depth = (answers.depth || [])[0];
  const undertones = answers.undertone || [];
  const fragrances = answers.fragrance || [];
  const concerns = answers.concern || [];
  const shade = computeShade(answers);
  const r = quiz.results;
  const items = [];

  const undertoneLabels = undertones.map((u) => optLabel('undertone', u)).filter(Boolean);
  const shadeReason = glow === 'gradual'
    ? fillTemplate(r.shadeReasonGradual, { shade })
    : fillTemplate(r.shadeReason, {
      shade,
      depth: (optLabel('depth', depth) || 'your').toLowerCase(),
      undertone: undertoneLabels.join(' & ').toLowerCase() || 'natural',
    });
  items.push({ category: 'shade', title: r.categories.shade, value: `Shade ${shade} of 12`, reason: shadeReason });

  if (glow !== 'gradual' && undertoneLabels.length) {
    items.push({ category: 'bronzer', title: r.categories.bronzer, value: undertoneLabels.join(', '), reason: undertones.map((u) => optReason('undertone', u)).filter(Boolean).join(' ') });
  }
  if (fragrances.length) {
    items.push({ category: 'scent', title: r.categories.scent, value: fragrances.map((f) => optLabel('fragrance', f)).join(', '), reason: fragrances.map((f) => optReason('fragrance', f)).filter(Boolean).join(' ') });
  }
  if (concerns.length) {
    items.push({ category: 'wellfit', title: r.categories.wellfit, value: concerns.map((c) => optLabel('concern', c)).join(', '), reason: concerns.map((c) => optReason('concern', c)).filter(Boolean).join(' ') });
  }
  return { shade, items };
}

const QuizPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const componentData = useSelector((state) => state?.tanProfile?.components);
  // Components are only needed to build/save the session (logged-in path).
  useGetComponentsQuery(undefined, { skip: !user });

  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stage, setStage] = useState('quiz'); // 'quiz' | 'results'

  // Restore answers + jump to results if we just came back from logging in.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(RESUME_KEY);
      if (saved) {
        setAnswers(JSON.parse(saved));
        setStage('results');
        sessionStorage.removeItem(RESUME_KEY);
        dispatch(clearResumeTo());
      }
    } catch (e) { /* ignore */ }
  }, [dispatch]);

  const visibleQuestions = useMemo(() => quiz.questions.filter((q) => {
    if (!q.condition) return true;
    const { question, notIn } = q.condition;
    const ans = answers[question] || [];
    if (!ans.length) return true;
    return !notIn.includes(ans[0]);
  }), [answers]);

  const currentQuestion = visibleQuestions[currentIndex];
  const isLast = currentIndex === visibleQuestions.length - 1;
  const currentAnswers = answers[currentQuestion?.id] || [];
  const requiresAnswer = currentQuestion?.mapsTo != null;
  const canNext = !requiresAnswer || currentAnswers.length > 0;
  const progress = ((currentIndex + 1) / visibleQuestions.length) * 100;

  const toggle = (questionId, optionId, isMulti, maxSelect) => {
    setAnswers((prev) => {
      const cur = prev[questionId] || [];
      if (isMulti) {
        if (cur.includes(optionId)) return { ...prev, [questionId]: cur.filter((id) => id !== optionId) };
        if (cur.length < maxSelect) return { ...prev, [questionId]: [...cur, optionId] };
        return prev;
      }
      return { ...prev, [questionId]: [optionId] };
    });
  };

  const findBySku = (sku) => {
    for (const compId in componentData) {
      for (const cv of Object.values(componentData[compId]?.component_values || {})) {
        if (cv.sku === sku) return { ...cv, component_id: parseInt(compId, 10) };
      }
    }
    return null;
  };

  // Logged-in save: build the session components and continue to confirmation.
  const saveProfile = () => {
    const sessionComponents = [];
    const glow = (answers.glow || [])[0];
    const undertones = answers.undertone || [];
    const fragrances = answers.fragrance || [];
    const concerns = answers.concern || [];
    const shade = computeShade(answers);

    const tanValues = Object.values(componentData?.['1']?.component_values || {});
    const tanEntry = tanValues.find((cv) => parseInt(cv.value, 10) === shade);
    if (tanEntry) sessionComponents.push({ value: String(shade), component_id: 1, component_value_id: tanEntry.id });

    const pushBySku = (qid, ids) => ids.forEach((id) => {
      const opt = findOption(qid, id);
      if (opt?.sku) {
        const cv = findBySku(opt.sku);
        if (cv) sessionComponents.push({ value: cv.value, component_id: cv.component_id, component_value_id: cv.id });
      }
    });
    if (glow !== 'gradual') pushBySku('undertone', undertones);
    pushBySku('fragrance', fragrances);
    pushBySku('concern', concerns);

    dispatch(setScannedBoothData(null));
    dispatch(setScannedBoothComponents(null));
    dispatch(setProfileMeta({ options: [], navigationDirection: 'new', sessionTypes: ['tan'] }));
    dispatch(setSelected({ session_component: sessionComponents }));
    navigate('/session/confirmSession');
  };

  // Logged-out: stash answers + come back here after login to save.
  const loginToSave = () => {
    try { sessionStorage.setItem(RESUME_KEY, JSON.stringify(answers)); } catch (e) { /* ignore */ }
    dispatch(setResumeTo('/quiz'));
    navigate('/login');
  };

  const onNext = () => {
    if (isLast) setStage('results');
    else setCurrentIndex((i) => i + 1);
  };

  if (stage === 'results') {
    const { items } = buildProfile(answers);
    return (
      <div className="quiz-page quiz-results">
        <h2 className="quiz-results-title">{quiz.results.title}</h2>
        <p className="quiz-results-subtitle">{quiz.results.subtitle}</p>

        <div className="quiz-results-list">
          {items.map((it) => (
            <div className="quiz-result-card" key={it.category}>
              <div className="quiz-result-title">{it.title}</div>
              <div className="quiz-result-value">{it.value}</div>
              {it.reason && <div className="quiz-result-reason">{it.reason}</div>}
            </div>
          ))}
        </div>

        <div className="quiz-results-actions">
          {user ? (
            <Button className="footer-button tan-style-button" onClick={saveProfile}>Save my profile</Button>
          ) : (
            <>
              <p className="quiz-results-cta">Log in to save this profile and start your session.</p>
              <Button className="footer-button tan-style-button" onClick={loginToSave}>Log in to save</Button>
            </>
          )}
          <Button className="footer-button" variant="secondary" onClick={() => { setStage('quiz'); setCurrentIndex(0); }}>Retake quiz</Button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  return (
    <div className="quiz-page">
      <div className="quiz-progress-bar">
        <div className="quiz-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="quiz-step">{currentIndex + 1} / {visibleQuestions.length}</div>

      <div className="quiz-body">
        <h2 className="quiz-question">{currentQuestion.question}</h2>
        {currentQuestion.subtitle && (
          <p className="quiz-subtitle">{currentQuestion.subtitle}</p>
        )}
        {currentQuestion.type === 'multi' && (
          <p className="quiz-multi-hint">Select up to {currentQuestion.maxSelect}</p>
        )}
        <div className="quiz-options">
          {currentQuestion.options.map((opt) => {
            const sel = currentAnswers.includes(opt.id);
            return (
              <div
                key={opt.id}
                className={`quiz-option ${sel ? 'selected' : ''}`}
                onClick={() => toggle(currentQuestion.id, opt.id, currentQuestion.type === 'multi', currentQuestion.maxSelect)}
              >
                <span className="quiz-option-label">{opt.label}</span>
                {opt.description && <span className="quiz-option-desc">{opt.description}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="footer">
        <Button
          className="footer-button"
          variant="secondary"
          onClick={() => setCurrentIndex((i) => i - 1)}
          disabled={currentIndex === 0}
        >Back</Button>
        <Button
          className="footer-button tan-style-button"
          onClick={onNext}
          disabled={!canNext}
        >{isLast ? 'See My Results' : 'Next'}</Button>
      </div>
    </div>
  );
};

export default QuizPage;
